import {invertRecord} from "../collection-utils";
import {type RevaValueParameterType, SEGMENT} from "../reva-spec";

/**
 * Which persisted subtab a parameter value came from. "avm" is the raw/live AVM-computed value;
 * a manual REVA override of it is exposed separately as `reva` on {@link ParameterValues} and, once
 * resolved, is reported as activeParameterType "reva" (see PERSISTENCE_TO_SOURCE / ALL_SOURCES below).
 */
export type ParameterSource =
    | "avm"
    | "firstFinalize"
    | "register"
    | "energy"
    | "idpEnergyCertificate"
    | "plot"
    | "inspectionDerived"
    | "inspection"
    | "initialValuation"
    | "immodrop";

/**
 * Persistence layer a parameter value is stored in — the upper-case counterpart of
 * {@link ParameterSource}. The members mirror the `parametersIn*` layers of the valuation payload
 * (`ValuationData`): "AVM" is `parametersInReva`, "INSPECTION" is `parametersInInspection`, and so
 * on. Seven of them are also a {@link RevaValueParameterType} — the layers a spec input row writes
 * to; "INSPECTION" and "INITIAL_VALUATION" are read-only and have neither a spec parameter type nor
 * a PATCH endpoint, while the spec's "META" and "DOC_PROPERTY" own no layer at all.
 *
 * Distinct from the client's `PersistableParameterType`, which describes what the REVA API accepts
 * in a PATCH and consequently has a different set of members.
 */
export type ParameterPersistenceType =
    | "AVM"
    | "FIRST_FINALIZE"
    | "REGISTER"
    | "ENERGY_CERT"
    | "IDP_ENERGY_CERTIFICATE"
    | "PLOT"
    | "INSPECTION_DERIVED"
    | "INSPECTION"
    | "INITIAL_VALUATION"
    | "IMMODROP";

/**
 * The technical anchor: the segment that applies when no layer carries one — not a choice anybody
 * made. The effective segment itself is resolved by the backend (`SegmentResolver` in reva-rest) and
 * arrives in `valuation.segment` (#19444).
 */
export const DEFAULT_SEGMENT = SEGMENT.WHG_K;

/**
 * #19092: a category param quality_furnishings {0,1,2,3,4} is mapped to the continuous param
 * quality_furnishings_continuous [1-5] via '+1', to represent conflicts between initial valuation
 * and inspection for this AVM-relevant parameter. Mutates initialValuationParams in place.
 */
export function deriveQualityFurnishingsContinuous(
    initialValuationParams: Record<string, any> | null | undefined,
): void {
    if (initialValuationParams?.quality_furnishings && !initialValuationParams.quality_furnishings_continuous) {
        initialValuationParams.quality_furnishings_continuous = initialValuationParams.quality_furnishings + 1;
    }
}

/** special_property_features_* parameter names (#18080). */
export const SPF_PARAMS = {
    values: "special_property_features_values",
    names: "special_property_features_names_de",
    valuesLending: "special_property_features_values_lending",
    shares: "special_property_features_shares",
};

/** Eigene Merkmal-Tabelle; verdrängt Besichtigung, Erstbewertung, Exposé und prop_pool/prop_sauna (#18080). */
export function hasSpfTable(inspectionDerived: Record<string, unknown> | null | undefined): boolean {
    return inspectionDerived?.[SPF_PARAMS.names] != null || inspectionDerived?.[SPF_PARAMS.values] != null;
}

/** Die vier Merkmal-Arrays einer Schicht, zeilengleich. */
export type SpfColumns = Record<string, unknown[] | null>;

/**
 * Bringt die Merkmal-Arrays einer Schicht auf die Zeilenzahl der Namen; Werte ohne Namen entfallen.
 * Beleihungswert und Anteil ohne einen einzigen Eintrag werden zu null. Ohne Tabelle: null.
 */
export function normalizeSpf(params: Record<string, unknown> | null | undefined): SpfColumns | null {
    if (!hasSpfTable(params)) return null;
    const names = params?.[SPF_PARAMS.names];
    const rowCount = Array.isArray(names) ? names.length : 0;
    const column = (param: string) => {
        const source = params?.[param];
        return Array.isArray(source) ? Array.from({length: rowCount}, (_, i) => source[i] ?? null) : null;
    };
    const optionalColumn = (param: string) => {
        const result = column(param);
        return result?.some((value) => value != null) ? result : null;
    };
    return {
        [SPF_PARAMS.names]: column(SPF_PARAMS.names) ?? Array(rowCount).fill(null),
        [SPF_PARAMS.values]: column(SPF_PARAMS.values) ?? Array(rowCount).fill(null),
        [SPF_PARAMS.valuesLending]: optionalColumn(SPF_PARAMS.valuesLending),
        [SPF_PARAMS.shares]: optionalColumn(SPF_PARAMS.shares),
    };
}

/**
 * Insertion order defines fallback priority (see resolveActiveParameterValue's otherSourcesEntries):
 * the first source below with a non-null value wins. IMMODROP comes last on purpose — it's the
 * lowest-priority (least trusted) source and only becomes active when no other source has a value.
 */
export const PERSISTENCE_TO_SOURCE = {
    AVM: "avm",
    FIRST_FINALIZE: "firstFinalize",
    REGISTER: "register",
    ENERGY_CERT: "energy",
    IDP_ENERGY_CERTIFICATE: "idpEnergyCertificate",
    PLOT: "plot",
    INSPECTION_DERIVED: "inspectionDerived",
    INSPECTION: "inspection",
    INITIAL_VALUATION: "initialValuation",
    IMMODROP: "immodrop",
} as const satisfies Record<ParameterPersistenceType, ParameterSource>;

/**
 * Source key → persistence layer, the inverse of {@link PERSISTENCE_TO_SOURCE}. The annotation
 * additionally asserts that every {@link ParameterSource} is covered.
 */
export const SOURCE_TO_PERSISTENCE: Record<ParameterSource, ParameterPersistenceType> =
    invertRecord(PERSISTENCE_TO_SOURCE);

/** All sources, ordered by resolution priority (highest first). */
export const ALL_SOURCES: ParameterSource[] = Object.values(PERSISTENCE_TO_SOURCE);

const NON_AVM_SOURCES = ALL_SOURCES.filter((key) => key !== "avm");

export interface ParameterValues {
    reva?: any;
    avm?: any;
    firstFinalize?: any;
    register?: any;
    energy?: any;
    idpEnergyCertificate?: any;
    plot?: any;
    inspectionDerived?: any;
    inspection?: any;
    initialValuation?: any;
    immodrop?: any;
    comment?: string | null;
    /** flag indicating whether the resolved value may be a conflicting one; defaults to true */
    useConflictingValue?: boolean;
    parameter: string;
    hasSpfTable?: boolean;
}

/**
 * Which layer an active value comes from, as reported after the resolution: a {@link ParameterSource},
 * plus "reva" for a manual override and "fallback" for the technical segment anchor no layer carries.
 *
 * "avm" is excluded on purpose: a value taken from that layer is reported as "reva" (see
 * {@link resolveActiveParameterValue}), so the raw source never reaches a caller.
 */
export type ResolvedParameterSource = Exclude<ParameterSource, "avm"> | "reva" | "fallback";

export interface ActiveParameterValue {
    /** value of the parameter */
    activeParameterValue: any;
    /** flag indicating if the reva value is empty */
    hasEmptyRevaValue: boolean;
    /** Which layer the active value comes from; `getResourceColor` turns it into the input's colour. */
    activeParameterType: ResolvedParameterSource | null;
    /** flag indicating if the parameter has a conflict */
    hasConflict: boolean;
    /** values of the parameter */
    allValues: ParameterValues;
    /** comment for the parameter */
    comment?: string | null;
}

/**
 * Parameter type the resolution runs for: either a type from the AVM spec or one of the two
 * persistence layers that has no spec counterpart ("INSPECTION", "INITIAL_VALUATION" — reached via
 * {@link SOURCE_TO_PERSISTENCE}, e.g. from the changelog).
 */
export type ResolvableParameterType = RevaValueParameterType | ParameterPersistenceType;

/** Subtabs whose own value is authoritative and never derived from another layer. */
const NON_DERIVABLE_PARAMETER_TYPES = ["REGISTER", "ENERGY_CERT", "PLOT"] as const;

function isNonDerivableParameterType(
    parameterType: ResolvableParameterType,
): parameterType is (typeof NON_DERIVABLE_PARAMETER_TYPES)[number] {
    return (NON_DERIVABLE_PARAMETER_TYPES as readonly string[]).includes(parameterType);
}

/**
 * Wert, mit dem ein aufgelöster Parameter in den AVM-Indikations-Payload geht, oder undefined, wenn er
 * dort fehlen soll (null, "empty"). section_ii_entry_value ist ein double[]; gespeicherte Bewertungen können
 * ihn noch als alten Skalar enthalten (0 = keine Abt.-II-Einträge), an dem die AVM mit „required data type:
 * Double[]-Array" die ganze Indikation scheitern lässt. Der leere Skalar entfällt deshalb, jeder andere wird
 * in ein Array gepackt (#19092).
 */
export function toAvmPayloadValue(parameter: string, value: unknown): unknown {
    if (value == null || value === "empty") return undefined;
    if (parameter === "section_ii_entry_value" && !Array.isArray(value)) return value === 0 ? undefined : [value];
    return value;
}

/**
 * Resolve the active parameter value based on priority and conflict rules.
 * Note: mutates `parameters` for the SPF/firstFinalize special cases below and returns it as
 * `allValues` — callers relying on `allValues` observe those mutations.
 */
export function resolveActiveParameterValue(
    parameterType: ResolvableParameterType,
    parameters: ParameterValues,
): ActiveParameterValue {
    const {comment, useConflictingValue, parameter, hasSpfTable} = parameters;
    let reva = parameters.reva;

    // Don't override parameters in the non-derivable subtabs; except leasehold (#18222)
    const subtabType = isNonDerivableParameterType(parameterType) ? PERSISTENCE_TO_SOURCE[parameterType] : null;

    if (subtabType && !parameter.startsWith("prop_leasehold")) {
        let value = parameters[subtabType];
        let activeSubtabType: ParameterSource = subtabType;
        // ENERGY_CERT: ein automatisch ausgelesener Wert (idpEnergyCertificate) dient nur als Fallback, falls kein
        // manuell gepflegter ENERGY_CERT-Wert vorliegt; der manuelle Wert hat immer Vorrang.
        if (parameterType === "ENERGY_CERT" && (value === null || value === undefined)) {
            const idpValue = parameters.idpEnergyCertificate;
            if (idpValue !== null && idpValue !== undefined) {
                value = idpValue;
                activeSubtabType = "idpEnergyCertificate";
            }
        }
        return {
            hasEmptyRevaValue: reva === "empty",
            hasConflict: false,
            allValues: parameters,
            comment,
            activeParameterValue: value === "empty" ? null : value,
            activeParameterType: value !== null && value !== undefined ? activeSubtabType : null,
        };
    }

    // #18080: eine eigene Merkmal-Tabelle verdrängt Besichtigung, Erstbewertung und Exposé, bei Pool/Sauna auch reva
    if (hasSpfTable && ["prop_sauna", "prop_pool", ...Object.values(SPF_PARAMS)].includes(parameter)) {
        parameters.initialValuation = null;
        parameters.inspection = null;
        parameters.immodrop = null;
        if (parameter === "prop_sauna" || parameter === "prop_pool") {
            parameters.reva = null;
            reva = null;
        }
    }
    // #18566: For special_property_features_values: ignore possible first finalize value, so it can be overwritten
    if (parameter === "special_property_features_values") parameters.firstFinalize = null;

    const conflictingValue = useConflictingValue ?? true;
    const revaDefined = reva !== null && reva !== undefined;
    // Priority 1: reva overrides
    if (revaDefined) {
        const type = ALL_SOURCES.find((key) => revaDefined && parameters[key] === reva);
        const activeType = type === "avm" ? "reva" : type;
        return {
            activeParameterValue: reva === "empty" ? null : reva,
            hasEmptyRevaValue: reva === "empty",
            activeParameterType: activeType ?? null,
            hasConflict: false,
            allValues: parameters,
            comment,
        };
    }
    // #19774: a manual ENERGY_CERT value (including "empty") overrides the IDP value of the same certificate without a
    // conflict, as in the energy certificate tab; the IDP value stays in allValues for the info icon.
    const energyDefined = parameters.energy !== null && parameters.energy !== undefined;
    // lookup other sources non empty values
    const otherSourcesEntries = NON_AVM_SOURCES.map((key) => ({type: key, value: parameters[key]}))
        .filter((v) => v.value !== undefined && v.value !== null)
        .filter((v) => !(energyDefined && v.type === "idpEnergyCertificate"))
        // only lookup values in inspection and inspectionDerived if parameterType is INSPECTION_DERIVED
        .filter(
            (v) => parameterType !== "INSPECTION_DERIVED" || ["inspection", "inspectionDerived"].indexOf(v.type) > -1,
        );

    // Set empty values if no otherSourceEntries can be found
    if (otherSourcesEntries.length === 0) {
        return {
            activeParameterValue: null,
            activeParameterType: null,
            hasConflict: false,
            allValues: parameters,
            comment,
            hasEmptyRevaValue: false,
        };
    }

    // Other source values defined, pick first as activeValue
    const activeValue = otherSourcesEntries[0].value;
    const activeType = otherSourcesEntries[0].type;

    // Multiple source values exist! check for conflict
    if (otherSourcesEntries.length > 0) {
        // #18071, #18192 to avoid false conflicts, map values to string, set numbers to 4 digits
        const conflictingValues = new Set(
            otherSourcesEntries.map((e) => (typeof e.value === "number" ? e.value.toFixed(4) : e.value.toString())),
        );
        const hasConflict = conflictingValues.size > 1;
        if (hasConflict) {
            //#17958: special case valuation_date: inspection value overrides without conflict
            if (parameter === "valuation_date" && parameters.inspection != null) {
                return {
                    activeParameterValue: parameters.inspection,
                    activeParameterType: "inspection",
                    hasConflict: false,
                    allValues: parameters,
                    comment,
                    hasEmptyRevaValue: false,
                };
            }
            //#18222/#18566/#19602: Bei Abt. II und Erbbaurecht gewinnt das Grundbuch ohne Konflikt gegen alle
            //Schichten; die Eingaben schreiben nur dorthin und könnten einen Konflikt nicht auflösen
            if (
                (parameter.startsWith("section_ii_entry_") || parameter.startsWith("prop_leasehold")) &&
                parameters.register != null
            ) {
                return {
                    activeParameterValue: parameters.register,
                    activeParameterType: "register",
                    hasConflict: false,
                    allValues: parameters,
                    comment,
                    hasEmptyRevaValue: false,
                };
            }
            //#18566: special case gross_floor_area: inspectionDerived overrides firstFinalize, same as section_ii_entry_value
            if (
                parameter === "gross_floor_area" &&
                parameters.inspectionDerived != null &&
                parameters.firstFinalize != null
            ) {
                return {
                    activeParameterValue: parameters.inspectionDerived,
                    activeParameterType: "inspectionDerived",
                    hasConflict: false,
                    allValues: parameters,
                    comment,
                    hasEmptyRevaValue: false,
                };
            }
            return {
                activeParameterValue: !conflictingValue ? null : activeValue,
                activeParameterType: !conflictingValue ? null : activeType,
                hasConflict: hasConflict && parameterType === "AVM",
                allValues: parameters,
                comment,
                hasEmptyRevaValue: false,
            };
        }

        return {
            // inspection has higher priority
            activeParameterValue: !conflictingValue && hasConflict ? null : activeValue,
            // if there is a conflict, set the type to initialValuation, otherwise to a combined type
            activeParameterType: !conflictingValue && hasConflict ? null : activeType,
            hasConflict,
            allValues: parameters,
            comment,
            hasEmptyRevaValue: false,
        };
    }

    // Only non conflicting values found, return first non empty value
    return {
        activeParameterValue: activeValue,
        activeParameterType: activeType,
        hasConflict: false,
        allValues: parameters,
        comment,
        hasEmptyRevaValue: false,
    };
}
